import { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { v4 as uuidv4 } from 'uuid';
import { resolveClientLocale } from 'lib/i18n/browser';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';

import {
  type ProfileCategory,
  type ProfileSkill,
  searchLanguageSkills,
  searchRelatedSkillsByCategories,
} from './profile.api';
import { withoutSkills } from './profile-exclusions';

interface SkillsAutocompleteProps {
  accessToken: string | null;
  selectedCategories?: ProfileCategory[];
  // Skills among the Profile's Exclusions; they are not offered as skills.
  excludedSkillUuids?: string[];
  value: ProfileSkill[];
  onChange: (skills: ProfileSkill[]) => void;
  disabled?: boolean;
}

function normalizeBrowserLocale() {
  return resolveClientLocale();
}

type SkillInputLocale = 'en' | 'uk';

function detectInputLocale(value: string): SkillInputLocale | null {
  const normalizedValue = value.trim();
  if (!normalizedValue.length) {
    return null;
  }

  // Treat Cyrillic skill input as Ukrainian for localization lookups.
  if (/[\u0400-\u04FF]/.test(normalizedValue)) {
    return 'uk';
  }

  if (/[a-z]/i.test(normalizedValue)) {
    return 'en';
  }

  return null;
}

function inferLocaleFromSelectedSkills(skills: ProfileSkill[]): SkillInputLocale | null {
  if (!skills.length) {
    return null;
  }

  let ukrainianScore = 0;
  let englishScore = 0;

  for (const skill of skills) {
    const inferredLocale = detectInputLocale(skill.title);
    if (inferredLocale === 'uk') {
      ukrainianScore += 1;
      continue;
    }

    if (inferredLocale === 'en') {
      englishScore += 1;
    }
  }

  if (ukrainianScore === englishScore) {
    return null;
  }

  return ukrainianScore > englishScore ? 'uk' : 'en';
}

function normalizeSkillTitle(value: string) {
  return value.trim().replace(/\s+/g, ' ').slice(0, 128);
}

function normalizeSkillKey(value: string) {
  const normalizedValue = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalizedValue.slice(0, 64);
}

function getSkillIdentity(skill: ProfileSkill) {
  const normalizedKey = normalizeSkillKey(skill.key);
  if (normalizedKey.length) {
    return `key:${normalizedKey}`;
  }

  const normalizedTitle = normalizeSkillTitle(skill.title).toLowerCase();
  if (normalizedTitle.length) {
    return `title:${normalizedTitle}`;
  }

  const normalizedUuid = skill.uuid.trim();
  return `uuid:${normalizedUuid}`;
}

function normalizeSkill(skill: ProfileSkill): ProfileSkill | null {
  const normalizedTitle = normalizeSkillTitle(skill.title);
  const trimmedKey = skill.key.trim().slice(0, 64);
  const normalizedUuid = skill.uuid.trim() || uuidv4();
  const normalizedTitleKey = normalizeSkillKey(normalizedTitle);
  const normalizedKey =
    trimmedKey ||
    normalizedTitleKey ||
    `skill-${normalizedUuid.replace(/-/g, '').slice(0, 12)}`;

  if (!normalizedTitle) {
    return null;
  }

  return {
    uuid: normalizedUuid,
    key: normalizedKey,
    title: normalizedTitle,
  };
}

function resolveTypedSkill(rawValue: string, skillsPool: ProfileSkill[]): ProfileSkill | null {
  const normalizedTitle = normalizeSkillTitle(rawValue);
  if (!normalizedTitle.length) {
    return null;
  }

  const normalizedKey = normalizeSkillKey(normalizedTitle);

  const normalizedTitleLowerCase = normalizedTitle.toLowerCase();
  const matchingSkill = skillsPool.find((skill) => {
    const normalizedSkillTitle = normalizeSkillTitle(skill.title).toLowerCase();
    const normalizedSkillKey = normalizeSkillKey(skill.key);
    return (
      normalizedSkillTitle === normalizedTitleLowerCase ||
      (normalizedKey.length > 0 && normalizedSkillKey === normalizedKey)
    );
  });

  if (matchingSkill) {
    return normalizeSkill(matchingSkill);
  }

  const generatedUuid = uuidv4();
  return {
    uuid: generatedUuid,
    key: normalizedKey || `skill-${generatedUuid.replace(/-/g, '').slice(0, 12)}`,
    title: normalizedTitle,
  };
}

function normalizeSelectedSkills(
  selectedSkills: Array<ProfileSkill | string>,
  skillsPool: ProfileSkill[]
) {
  const uniqueSkills = new Map<string, ProfileSkill>();

  for (const selectedSkill of selectedSkills) {
    const normalizedSkill =
      typeof selectedSkill === 'string'
        ? resolveTypedSkill(selectedSkill, skillsPool)
        : normalizeSkill(selectedSkill);
    if (!normalizedSkill) {
      continue;
    }

    const skillIdentity = getSkillIdentity(normalizedSkill);
    if (uniqueSkills.has(skillIdentity)) {
      continue;
    }

    uniqueSkills.set(skillIdentity, normalizedSkill);
  }

  return Array.from(uniqueSkills.values());
}

function mergeSkillOptions(primary: ProfileSkill[], secondary: ProfileSkill[]) {
  const byIdentity = new Map<string, ProfileSkill>();

  for (const skill of primary) {
    const normalizedSkill = normalizeSkill(skill);
    if (!normalizedSkill) {
      continue;
    }
    byIdentity.set(getSkillIdentity(normalizedSkill), normalizedSkill);
  }
  for (const skill of secondary) {
    const normalizedSkill = normalizeSkill(skill);
    if (!normalizedSkill) {
      continue;
    }
    byIdentity.set(getSkillIdentity(normalizedSkill), normalizedSkill);
  }

  return Array.from(byIdentity.values());
}

export function SkillsAutocomplete({
  accessToken,
  selectedCategories = [],
  excludedSkillUuids = [],
  value,
  onChange,
  disabled = false,
}: SkillsAutocompleteProps) {
  const locale = useLocale();
  const t = getTranslations(locale).adminInputs;
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<ProfileSkill[]>([]);
  const [fetchedCategoryRelatedOptions, setFetchedCategoryRelatedOptions] = useState<
    ProfileSkill[]
  >([]);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCategoryRelatedLoading, setIsCategoryRelatedLoading] = useState(false);
  const browserLocale = useMemo(() => normalizeBrowserLocale(), []);
  const selectedSkillsLocale = useMemo(() => inferLocaleFromSelectedSkills(value), [value]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery.length) {
      setOptions([]);
      setIsOptionsOpen(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const timerId = window.setTimeout(() => {
      setIsLoading(true);
      const queryLocale = detectInputLocale(normalizedQuery) ?? selectedSkillsLocale ?? browserLocale;

      searchLanguageSkills(normalizedQuery, queryLocale, accessToken ?? undefined)
        .then((skills) => {
          if (cancelled) {
            return;
          }
          const normalizedSkills = mergeSkillOptions(skills, []);
          setOptions(normalizedSkills);
          setIsOptionsOpen(normalizedSkills.length > 0);
        })
        .catch(() => {
          if (cancelled) {
            return;
          }
          setOptions([]);
          setIsOptionsOpen(false);
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [query, selectedSkillsLocale, browserLocale, accessToken]);

  useEffect(() => {
    const selectedCategoryUuids = Array.from(
      new Set(selectedCategories.map((category) => category.uuid.trim()).filter(Boolean))
    );

    if (!selectedCategoryUuids.length) {
      setFetchedCategoryRelatedOptions([]);
      setIsCategoryRelatedLoading(false);
      return;
    }

    let cancelled = false;
    setIsCategoryRelatedLoading(true);
    const relatedLocale = browserLocale;

    searchRelatedSkillsByCategories(
      selectedCategoryUuids,
      relatedLocale,
      accessToken ?? undefined
    )
      .then((skills) => {
        if (cancelled) {
          return;
        }

        const normalizedSkills = mergeSkillOptions(skills, []);
        setFetchedCategoryRelatedOptions(normalizedSkills);
      })
      .catch(() => {
        if (!cancelled) {
          setFetchedCategoryRelatedOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsCategoryRelatedLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCategories, browserLocale, accessToken]);

  const categoryRelatedOptions = useMemo(() => {
    if (!fetchedCategoryRelatedOptions.length) {
      return [];
    }

    const selectedSkillIdentities = new Set(
      value.map((selectedSkill) => getSkillIdentity(selectedSkill))
    );

    return withoutSkills(fetchedCategoryRelatedOptions, excludedSkillUuids).filter(
      (skill) => !selectedSkillIdentities.has(getSkillIdentity(skill))
    );
  }, [excludedSkillUuids, fetchedCategoryRelatedOptions, value]);

  const visibleOptions = useMemo(
    () => withoutSkills(options, excludedSkillUuids),
    [excludedSkillUuids, options]
  );

  const handleAddRelatedSkill = (skill: ProfileSkill) => {
    const nextSelectedSkills = normalizeSelectedSkills(
      [...value, skill],
      mergeSkillOptions(options, categoryRelatedOptions)
    );
    onChange(nextSelectedSkills);
    setOptions((currentOptions) => mergeSkillOptions(currentOptions, [skill]));
  };

  return (
    <Box>
      <Autocomplete<ProfileSkill, true, false, true>
        multiple
        freeSolo
        options={visibleOptions}
        value={value}
        disabled={disabled}
        loading={isLoading}
        open={isOptionsOpen && visibleOptions.length > 0}
        onOpen={() => {
          if (options.length > 0) {
            setIsOptionsOpen(true);
          }
        }}
        onClose={() => {
          setIsOptionsOpen(false);
        }}
        filterOptions={(filteredOptions) => filteredOptions}
        isOptionEqualToValue={(option, selectedSkill) =>
          typeof selectedSkill !== 'string' && option.uuid === selectedSkill.uuid
        }
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.title
        }
        onInputChange={(_, nextQuery, reason) => {
          if (reason === 'input') {
            setQuery(nextQuery);
            if (!nextQuery.trim().length) {
              setIsOptionsOpen(false);
            }
          } else if (reason === 'clear' || reason === 'reset') {
            setQuery('');
            setIsOptionsOpen(false);
          }
        }}
        onChange={(_, nextSkills) => {
          const normalizedSkills = normalizeSelectedSkills(
            nextSkills,
            mergeSkillOptions(options, value)
          );
          onChange(normalizedSkills);
          setOptions((currentOptions) =>
            mergeSkillOptions(currentOptions, normalizedSkills)
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t.searchSkillsPlaceholder}
            helperText={t.searchSkillsHelper}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {isCategoryRelatedLoading ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          {t.relatedSkillsLoading}
        </Typography>
      ) : null}

      {!isCategoryRelatedLoading && categoryRelatedOptions.length > 0 ? (
        <Box sx={{ mt: 1.25 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.75 }}
          >
            {t.relatedSkillsTitle}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {categoryRelatedOptions.map((relatedSkill) => (
              <Chip
                key={`category-related-${getSkillIdentity(relatedSkill)}`}
                label={relatedSkill.title}
                size="small"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleAddRelatedSkill(relatedSkill)}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
